import { FileText, Landmark, UserPlus, Building2, Mail, Phone, ShieldQuestion, Users } from "lucide-react";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  Button,
  Pill,
  Avatar,
  SkeletonText,
} from "@/design/components";
import { formatRelativeShort } from "@/design/format";
import { useSeed, usePeopleIndex } from "@/domain/hooks";
import { useGateDetail } from "@/domain/useOnboarding";
import { roleLabelFor } from "@/domain/cascadeRoles";
import { demoModeActive, type GateDetail } from "@/domain/api";
import type { SecureInviteLink } from "@/domain/types";

// The gate row carries only id/name/tier plus the summary fields the queue
// endpoint returns. The full submission — what the contractor actually typed and
// uploaded — comes from GET /onboarding/gates/:orgId (real mode) or the seed
// store (demo mode); both are normalised into one `GateView` below so the drawer
// renders a single way.
export type GateRow = {
  id: string;
  name: string;
  tier: string;
  /** Summary fields carried from GET /onboarding/gates (used until the detail lands). */
  legalName?: string;
  kycState?: string;
  bankState?: string;
};

const BANK_STATE: Record<string, { label: string; variant: "success" | "info" | "warn" | "neutral" }> = {
  not_started: { label: "Not started", variant: "neutral" },
  pending: { label: "Submitted — awaiting Stratus confirmation", variant: "info" },
  manual_submitted: { label: "Statement submitted — manual review", variant: "info" },
  open_banking_verified: { label: "Verified via open banking", variant: "success" },
  mobile_money_verified: { label: "Verified via mobile money", variant: "success" },
  verified: { label: "Verified", variant: "success" },
  failed: { label: "Verification failed", variant: "warn" },
};

const BANK_METHOD: Record<string, string> = {
  statement: "Bank statement upload",
  open_banking: "Open banking connection",
  mobile_money: "Mobile money account",
  manual: "Manual review",
};

const INVITE_TIER: Record<SecureInviteLink["tier"], string> = {
  prime: "Prime lead",
  sub: "Subcontractor lead",
  staff: "Team member",
  reporter: "Reporter",
};

const INVITE_STATUS: Record<string, { label: string; variant: "success" | "info" | "warn" | "neutral" | "danger" }> = {
  queued: { label: "Queued — sends on approval", variant: "warn" },
  sent: { label: "Sent", variant: "info" },
  pending: { label: "Sent", variant: "info" },
  forwarded: { label: "Forwarded", variant: "info" },
  redeemed: { label: "Accepted", variant: "success" },
  expired: { label: "Expired", variant: "warn" },
  revoked: { label: "Revoked", variant: "neutral" },
};

// What the drawer renders, whichever source filled it in.
type GateView = {
  legalName: string;
  fields: { label: string; value: string }[];
  contact: { name: string; role?: string; email?: string; phone?: string; initials: string } | null;
  bank: { stateKey: string | null; method: string | null; statementFile: GateDetail["bank"]["statementFile"]; documentHref?: string };
  documents: {
    key: string;
    label: string;
    required: boolean;
    accepted: boolean;
    submitted: boolean;
    detail?: string | null;
    fileName?: string | null;
    href?: string;
  }[];
  invites: { id: string; email: string; roleLabel: string; scope?: string | null; status: string }[];
  members: { id: string; name: string; email: string; roleLabel?: string | null }[];
  queries: { id: string; body: string; meta: string }[];
  awaitingSignOff: boolean;
  daysInStage: number | null;
};

export function OnboardingDetailDrawer({
  gate,
  open,
  onOpenChange,
  onApprove,
  onReject,
}: {
  gate: GateRow | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onApprove: (g: GateRow) => void;
  onReject: (g: GateRow) => void;
}) {
  const demo = demoModeActive();
  const seed = useSeed();
  const people = usePeopleIndex();
  // Off demo the submission is fetched; the query is disabled in demo mode and
  // while the drawer is closed, so opening a row is what triggers the load.
  const detail = useGateDetail(open && gate && !demo ? gate.id : null);

  if (!gate) return null;

  const view = demo
    ? seedView(gate, seed, people)
    : detail.data
      ? apiView(detail.data)
      : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="720px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{gate.name}</DrawerTitle>
        <div className="mt-1 flex items-center gap-2">
          <Pill variant={gate.tier === "prime" ? "brand" : "neutral"}>{gate.tier === "prime" ? "Prime" : "Subcontractor"}</Pill>
          {view?.awaitingSignOff && <Pill variant="warn">Awaiting sign-off</Pill>}
          {view?.daysInStage != null && <span className="text-xs text-faint">{view.daysInStage} days in stage</span>}
        </div>
      </DrawerHeader>

      <DrawerBody className="space-y-6">
        {!view && detail.isLoading && (
          <div className="space-y-4" aria-busy>
            <SkeletonText lines={4} />
            <SkeletonText lines={3} />
            <SkeletonText lines={3} />
          </div>
        )}

        {!view && !detail.isLoading && (
          <p className="rounded-md border border-line bg-subtle/60 px-3 py-2 text-sm text-mute">
            This onboarding submission couldn't be loaded
            {detail.error ? " — the detail request failed." : "."} Showing the queue summary only:{" "}
            {gate.legalName ?? gate.name}
            {gate.kycState ? ` · KYC ${kycStatusLabel(gate.kycState)}` : ""}
            {gate.bankState ? ` · bank ${bankLabel(gate.bankState)}` : ""}.
          </p>
        )}

        {view && (
          <>
            {/* Organisation identity + primary contact */}
            <section>
              <SectionTitle icon={<Building2 className="h-3.5 w-3.5" />}>Organisation</SectionTitle>
              <dl className="grid grid-cols-2 gap-2">
                {view.fields.map((f) => (
                  <Field key={f.label} label={f.label} value={f.value} />
                ))}
              </dl>
              {view.contact && (
                <div className="mt-2 flex items-center gap-3 rounded-md border border-line bg-subtle/40 px-3 py-2">
                  <Avatar initials={view.contact.initials} size={32} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">
                      {view.contact.name}
                      {view.contact.role && <span className="font-normal text-faint"> · {view.contact.role}</span>}
                    </p>
                    <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-faint">
                      {view.contact.email && (
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {view.contact.email}</span>
                      )}
                      {view.contact.phone && (
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {view.contact.phone}</span>
                      )}
                    </p>
                  </div>
                  <Pill variant="outline" className="ml-auto">Primary contact</Pill>
                </div>
              )}
            </section>

            {/* Bank verification */}
            <section>
              <SectionTitle icon={<Landmark className="h-3.5 w-3.5" />}>Bank verification</SectionTitle>
              {view.bank.stateKey ? (
                <div className="rounded-md border border-line px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ink">Account verification</span>
                    <div className="flex items-center gap-2">
                      <Pill variant={bankVariant(view.bank.stateKey)}>{bankLabel(view.bank.stateKey)}</Pill>
                      {view.bank.documentHref && (
                        <a
                          href={view.bank.documentHref}
                          target="_blank"
                          rel="noreferrer"
                          className="focus-ring inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs text-mute hover:bg-subtle"
                        >
                          <FileText className="h-3.5 w-3.5" /> Bank statement
                        </a>
                      )}
                    </div>
                  </div>
                  {(view.bank.method || view.bank.statementFile) && (
                    <p className="mt-1 text-xs text-faint">
                      {view.bank.method ? BANK_METHOD[view.bank.method] ?? view.bank.method : null}
                      {view.bank.statementFile?.name ? ` · ${view.bank.statementFile.name}` : ""}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-faint">No bank verification recorded.</p>
              )}
            </section>

            {/* Documents submitted */}
            <section>
              <SectionTitle icon={<FileText className="h-3.5 w-3.5" />}>Documents submitted</SectionTitle>
              {view.documents.length > 0 ? (
                <ul className="space-y-2">
                  {view.documents.map((d) => (
                    <li key={d.key} className="flex items-center gap-2 rounded-md border border-line p-3">
                      <FileText className="h-4 w-4 shrink-0 text-faint" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ink">{d.label}</span>
                        {(d.fileName || d.detail) && (
                          <span className="block truncate text-xs text-faint">
                            {[d.fileName, d.detail].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </span>
                      {!d.required && <Pill variant="outline">Optional</Pill>}
                      {d.accepted && <Pill variant="success">Accepted</Pill>}
                      {d.submitted ? (
                        d.href ? (
                          <a
                            href={d.href}
                            target="_blank"
                            rel="noreferrer"
                            className="focus-ring inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-xs text-mute hover:bg-subtle"
                          >
                            Open
                          </a>
                        ) : (
                          <Pill variant="info">Submitted</Pill>
                        )
                      ) : (
                        <Pill variant="neutral">Missing</Pill>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-faint">No documents were submitted with this onboarding.</p>
              )}
            </section>

            {/* Team invites — people they want to invite */}
            <section>
              <SectionTitle icon={<UserPlus className="h-3.5 w-3.5" />}>People they want to invite</SectionTitle>
              {view.invites.length > 0 ? (
                <ul className="divide-y divide-hairline rounded-md border border-line">
                  {view.invites.map((inv) => {
                    const status = INVITE_STATUS[inv.status] ?? { label: cap(inv.status), variant: "neutral" as const };
                    return (
                      <li key={inv.id} className="flex items-center gap-3 px-3 py-2.5">
                        <Avatar initials={initialsFromEmail(inv.email)} size={28} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink">{inv.email || "Invite pending recipient"}</p>
                          <p className="truncate text-xs text-faint">
                            {inv.roleLabel}
                            {inv.scope ? ` · ${inv.scope}` : ""}
                          </p>
                        </div>
                        <Pill variant={status.variant}>{status.label}</Pill>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-faint">No team invites have been set up yet.</p>
              )}
            </section>

            {/* Accounts already created for this organisation */}
            {view.members.length > 0 && (
              <section>
                <SectionTitle icon={<Users className="h-3.5 w-3.5" />}>Accounts created</SectionTitle>
                <ul className="divide-y divide-hairline rounded-md border border-line">
                  {view.members.map((m) => (
                    <li key={m.id} className="flex items-center gap-3 px-3 py-2.5">
                      <Avatar initials={initialsFromName(m.name, m.email)} size={28} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{m.name || m.email}</p>
                        <p className="truncate text-xs text-faint">{m.email}</p>
                      </div>
                      {m.roleLabel && <Pill variant="outline">{m.roleLabel}</Pill>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Reviewer queries raised during KYC */}
            {view.queries.length > 0 && (
              <section>
                <SectionTitle icon={<ShieldQuestion className="h-3.5 w-3.5" />}>Queries raised</SectionTitle>
                <ul className="space-y-2">
                  {view.queries.map((c) => (
                    <li key={c.id} className="rounded-md bg-subtle px-3 py-2 text-sm text-mute">
                      {c.body}
                      <span className="mt-1 block text-[11px] text-faint">{c.meta}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </DrawerBody>

      <DrawerFooter>
        <Button variant="ghost" onClick={() => onReject(gate)}>Reject</Button>
        <Button onClick={() => onApprove(gate)}>Approve gate</Button>
      </DrawerFooter>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Source adapters
// ---------------------------------------------------------------------------

// Real mode: the submission as the backend recorded it. Registration ID, tax ID
// and country of incorporation are typed into the wizard and kept on the
// onboarding session, so they only reach the reviewer through this endpoint.
function apiView(d: GateDetail): GateView {
  const org = d.organisation;
  const submittedAt = d.submission ? d.submission.completedAt || d.submission.updatedAt : null;
  const fields = [
    { label: "Legal name", value: org.legalName || org.name },
    { label: "Trading name", value: org.tradingName || org.name },
    { label: "Registration ID", value: org.registrationId || "—" },
    { label: "Tax / VAT identifier", value: org.taxId || "—" },
    { label: "Country of incorporation", value: org.countryOfIncorporation || "—" },
    { label: "Status", value: cap(org.status).replace(/_/g, " ") },
    { label: "KYC status", value: kycStatusLabel(org.kycState) },
    { label: "Submitted", value: submittedAt ? formatRelativeShort(submittedAt) : "—" },
  ];
  if (org.parentOrganisationName) {
    fields.push({ label: "Invited by", value: org.parentOrganisationName });
  }

  return {
    legalName: org.legalName || org.name,
    fields,
    contact: d.primaryContact.name || d.primaryContact.email
      ? {
          name: d.primaryContact.name || (d.primaryContact.email as string),
          role: d.primaryContact.userType ? cap(d.primaryContact.userType.toLowerCase()).replace(/_/g, " ") : undefined,
          email: d.primaryContact.email ?? undefined,
          phone: d.primaryContact.phone ?? undefined,
          initials: initialsFromName(d.primaryContact.name, d.primaryContact.email),
        }
      : null,
    bank: {
      stateKey: d.bank.state || null,
      method: d.bank.method,
      statementFile: d.bank.statementFile,
    },
    documents: d.documents.map((doc) => ({
      key: doc.key,
      label: doc.label,
      required: doc.required,
      accepted: doc.accepted,
      submitted: doc.submitted,
      detail: doc.detail,
      fileName: doc.fileName,
    })),
    invites: d.invites.map((inv) => ({
      id: inv.id,
      email: inv.email,
      roleLabel: roleLabelFor(inv.role),
      scope: inv.projectName,
      status: inv.status,
    })),
    members: d.members.map((m) => ({
      id: String(m.userId),
      name: m.name || m.email,
      email: m.email,
      roleLabel: m.scope ? cap(m.scope) : null,
    })),
    queries: (d.kyc?.comments ?? []).map((c) => ({
      id: c.id,
      body: c.body,
      meta: `${c.visibility === "internal" ? "Internal Stratus" : "Shared with contractor"}${c.authorName ? ` · ${c.authorName}` : ""} · ${formatRelativeShort(c.createdAt)}`,
    })),
    awaitingSignOff:
      org.status === "awaiting_approval_gate" || org.pipelineStage === "awaiting_gate",
    daysInStage: org.daysInStage,
  };
}

// Demo mode: the same view assembled from the seed store, which is where the
// demo scenario's onboarding submissions live.
function seedView(
  gate: GateRow,
  seed: ReturnType<typeof useSeed>,
  people: ReturnType<typeof usePeopleIndex>,
): GateView | null {
  const org = seed.organisations.find((o) => o.id === gate.id);
  if (!org) return null;
  const kyc = seed.kycReviews.find((k) => k.organisationId === gate.id);
  const invites = (seed.inviteLinks ?? []).filter((i) => i.organisationId === gate.id);
  const contact = people[org.primaryContactPersonId];
  const projectName = (id: string) => seed.projects.find((p) => p.id === id)?.name ?? id;
  const structured = kyc?.structuredFields ?? [];
  const structuredValue = (label: string) => structured.find((f) => f.label === label)?.value;

  const fields = [
    { label: "Legal name", value: structuredValue("Legal name") ?? gate.legalName ?? gate.name },
    { label: "Registration ID", value: structuredValue("Registration ID") ?? "—" },
    { label: "Status", value: cap(org.status) },
    { label: "KYC status", value: kycStatusLabel(org.kycState) },
    ...structured.filter((f) => f.label !== "Legal name" && f.label !== "Registration ID"),
  ];

  return {
    legalName: structuredValue("Legal name") ?? gate.name,
    fields,
    contact: contact
      ? {
          name: contact.fullName,
          role: contact.role,
          email: contact.email,
          phone: contact.phone,
          initials: contact.avatarInitials,
        }
      : null,
    bank: {
      stateKey: org.bankVerificationState,
      method: null,
      statementFile: null,
      documentHref:
        org.bankVerificationState === "manual_submitted" ? "/seed-evidence/document.svg" : undefined,
    },
    documents: (kyc?.requiredDocuments ?? []).map((doc) => ({
      key: doc.key,
      label: doc.label,
      required: doc.required,
      accepted: doc.accepted,
      submitted: doc.documentId != null,
      href: doc.documentId != null ? "/seed-evidence/document.svg" : undefined,
    })),
    invites: invites.map((inv) => ({
      id: inv.id,
      email: inv.recipientEmail ?? "",
      roleLabel: INVITE_TIER[inv.tier],
      scope: inv.projectScopeIds.length > 0 ? inv.projectScopeIds.map(projectName).join(", ") : null,
      status: inv.status,
    })),
    members: [],
    queries: (kyc?.reviewerComments ?? []).map((c) => ({
      id: c.id,
      body: c.body,
      meta: `${c.visibility === "internal" ? "Internal Stratus" : "Shared with contractor"} · ${formatRelativeShort(c.createdAt)}`,
    })),
    awaitingSignOff: org.pipelineStage === "awaiting_gate",
    daysInStage: org.daysInStage ?? null,
  };
}

// ---------------------------------------------------------------------------

function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
      {icon}
      {children}
    </h4>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-subtle px-3 py-2">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}

function bankLabel(state: string): string {
  return BANK_STATE[state]?.label ?? cap(state).replace(/_/g, " ");
}

function bankVariant(state: string): "success" | "info" | "warn" | "neutral" {
  return BANK_STATE[state]?.variant ?? "neutral";
}

function kycStatusLabel(state: string | undefined): string {
  return state ? cap(state).replace(/_/g, " ") : "—";
}

function initialsFromEmail(email?: string): string {
  if (!email) return "?";
  const name = email.split("@")[0];
  const parts = name.split(/[.\-_]/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

function initialsFromName(name?: string | null, email?: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return initialsFromEmail(email ?? undefined);
  return (parts[0][0] ?? "?").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
